import React, { useMemo, useState } from "react";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { WorkspaceStateResponse, WorkspaceSummary } from "../../api/v2/types";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { buildWorkspaceKeyFromName } from "../shared/utils/keyUtils";

interface WorkspaceTabProps {
  workspaces: WorkspaceSummary[];
  workspaceKey: string;
  state?: WorkspaceStateResponse | null;
  isBusy?: boolean;
  onCreateWorkspace: (payload: {
    workspaceKey: string;
    workspaceName: string;
    description?: string;
  }) => Promise<void>;
  onRenameWorkspace: (payload: { workspaceName: string; description?: string }) => Promise<void>;
  onDeleteWorkspace: () => Promise<void>;
  onRefreshState: () => Promise<void>;
  groupsSlot?: React.ReactNode;
}

const WorkspaceTab = ({
  workspaces,
  workspaceKey,
  state,
  isBusy = false,
  onCreateWorkspace,
  onRenameWorkspace,
  onDeleteWorkspace,
  onRefreshState,
  groupsSlot,
}: WorkspaceTabProps) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [renameName, setRenameName] = useState("");
  const [renameDescription, setRenameDescription] = useState("");

  const newWorkspaceKey = useMemo(() => buildWorkspaceKeyFromName(newName), [newName]);

  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.workspaceKey === workspaceKey),
    [workspaces, workspaceKey]
  );

  const stateLabel = state
    ? state.stale
      ? `Stale cache · unbound ${state.stats.unboundJsonCount}`
      : `Fresh cache · bound ${state.stats.boundFiles}/${state.stats.jsonFilesInScope}`
    : "State unavailable";

  const openRenameDialog = () => {
    setRenameName(activeWorkspace?.workspaceName || "");
    setRenameDescription(activeWorkspace?.description || "");
    setRenameOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="rounded border p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={isBusy}>
                <Plus className="mr-1 h-4 w-4" /> Create workspace
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create workspace</DialogTitle>
                <DialogDescription>
                  Workspace key is generated automatically from workspace name.
                </DialogDescription>
              </DialogHeader>
              <Input
                placeholder="Workspace name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
              />
              <Input
                readOnly
                value={newWorkspaceKey}
                placeholder="Generated workspace key"
                className="bg-muted"
              />
              <Input
                placeholder="Description (optional)"
                value={newDescription}
                onChange={(event) => setNewDescription(event.target.value)}
              />
              <DialogFooter>
                <Button
                  disabled={isBusy || !newName.trim()}
                  onClick={async () => {
                    await onCreateWorkspace({
                      workspaceKey: newWorkspaceKey,
                      workspaceName: newName.trim(),
                      description: newDescription.trim() || undefined,
                    });
                    setCreateOpen(false);
                    setNewName("");
                    setNewDescription("");
                  }}
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button variant="outline" onClick={openRenameDialog} disabled={isBusy}>
            <Pencil className="mr-1 h-4 w-4" /> Edit workspace
          </Button>
          <Button variant="outline" onClick={onDeleteWorkspace} disabled={isBusy}>
            <Trash2 className="mr-1 h-4 w-4" /> Delete workspace
          </Button>
          <Button variant="outline" onClick={onRefreshState} disabled={isBusy}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh state
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">{stateLabel}</p>
      </div>

      {groupsSlot}

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit workspace</DialogTitle>
            <DialogDescription>Update workspace display name and description.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Workspace name"
            value={renameName}
            onChange={(event) => setRenameName(event.target.value)}
          />
          <Input
            placeholder="Description (optional)"
            value={renameDescription}
            onChange={(event) => setRenameDescription(event.target.value)}
          />
          <DialogFooter>
            <Button
              disabled={isBusy || !renameName.trim()}
              onClick={async () => {
                await onRenameWorkspace({
                  workspaceName: renameName.trim(),
                  description: renameDescription.trim() || undefined,
                });
                setRenameOpen(false);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkspaceTab;
